import { useContext, useRef, useState } from 'react';

import { Icon } from '../Icon';
import { TimelineContext } from './Timeline';

import { assert } from '../../utils/assert';

import styles from './TimelineHandle.module.scss';

interface TimelineHandleProps {
    time: Date;
    onChange: (time: Date) => void;
}
const DATE_FORMAT = new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
});

export const TimelineHandle = ({ time, onChange }: TimelineHandleProps) => {
    const context = useContext(TimelineContext);
    assert(context, 'Must be used within Timeline component');

    const { pixelsPerMs, isVisible, timeToPosition, laneHeight, timescale } =
        context;
    const pointerDownRef = useRef<{ mouseX: number } | null>(null);
    const [isMoving, setIsMoving] = useState(false);

    if (!isVisible(time.getTime())) {
        return null;
    }

    return (
        <>
            <div
                style={{
                    left: timeToPosition(time.getTime()),
                }}
                className={styles.line}
            />
            <div
                className={styles.textContainer}
                style={{
                    top: laneHeight,
                    left: timeToPosition(time.getTime()),
                    cursor: isMoving ? 'grabbing' : undefined,
                }}
                onPointerDown={(e) => {
                    if (e.button !== 0) {
                        // Only trigger on left mouse button
                        return;
                    }
                    setIsMoving(true);
                    e.currentTarget.setPointerCapture(e.pointerId);
                    pointerDownRef.current = {
                        mouseX: e.nativeEvent.offsetX,
                    };
                }}
                onPointerMove={(e) => {
                    if (!pointerDownRef.current) {
                        return;
                    }
                    const delta =
                        e.nativeEvent.offsetX - pointerDownRef.current.mouseX;

                    const deltaMs = delta / pixelsPerMs;
                    const newTime = new Date(time.getTime() + deltaMs);
                    onChange(timescale.roundTo(newTime));
                }}
                onPointerUp={(e) => {
                    setIsMoving(false);
                    e.currentTarget.releasePointerCapture(e.pointerId);
                    pointerDownRef.current = null;
                }}
            >
                <Icon name="drag_indicator" />
                <p className={styles.text}>{DATE_FORMAT.format(time)}</p>
            </div>
        </>
    );
};
